import { useEffect, useRef, useState, type ReactNode } from "react";
import { Grid2X2, MoveLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

type TestimonialsTapeProps<T> = {
  id: string;
  label: string;
  items: readonly T[];
  itemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  gridClassName?: string;
  itemClassName?: string;
  alignHeaders?: boolean;
};

/** Shared homepage presentation; the static list contains each author once. */
export function TestimonialsTape<T>({
  id,
  label,
  items,
  itemKey,
  renderItem,
  gridClassName = "md:grid-cols-2 xl:grid-cols-3",
  itemClassName = "w-[19rem] sm:w-[21rem]",
  alignHeaders = false,
}: TestimonialsTapeProps<T>) {
  const [showAll, setShowAll] = useState(false);
  const [touchPaused, setTouchPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!alignHeaders || !container) return;

    // Measure natural signature height, not the aligned wrapper, so resizing
    // can shrink as well as grow. Expanded quotes keep their own heights.
    const headers = Array.from(container.querySelectorAll<HTMLElement>("[data-testimonial-header] > div"));
    const align = () => {
      const height = Math.max(0, ...headers.map((header) => header.getBoundingClientRect().height));
      container.style.setProperty("--testimonial-header-height", `${height}px`);
    };
    // Delay the first measurement to the next frame: fonts are then loaded,
    // so the server-rendered layout and the hydrated layout match.
    const frame = requestAnimationFrame(align);
    const observer = new ResizeObserver(align);
    headers.forEach((header) => observer.observe(header));
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [alignHeaders, showAll, items]);

  return (
    <div ref={containerRef} className={alignHeaders ? "testimonials-aligned" : undefined}>
      <div className="mb-4 flex justify-end">
        <Button
          type="button"
          variant="link"
          aria-expanded={showAll}
          aria-controls={id}
          onClick={() => setShowAll((value) => !value)}
          className="min-h-11 gap-2 px-0 text-base text-ink underline underline-offset-4 hover:text-coral-text"
        >
          {showAll ? <MoveLeft aria-hidden="true" /> : <Grid2X2 aria-hidden="true" />}
          {showAll ? "Wróć do taśmy" : "Zobacz wszystkie"}
        </Button>
      </div>
      <div
        id={id}
        role="region"
        aria-label={label}
        className={showAll ? undefined : "testimonials-tape marquee"}
        data-paused={touchPaused ? "true" : undefined}
        onPointerDown={(event) => {
          if (event.pointerType === "touch") setTouchPaused(true);
        }}
      >
        <div className={showAll ? undefined : "marquee-inner flex w-max"}>
          <ul
            className={showAll
              ? `grid items-start gap-5 ${gridClassName}`
              : "marquee-track flex w-max shrink-0 items-start gap-5 pr-5"}
          >
            {items.map((item) => (
              <li key={itemKey(item)} className={showAll ? "min-w-0" : `${itemClassName} shrink-0`}>
                {renderItem(item)}
              </li>
            ))}
          </ul>
          {!showAll && (
            <ul
              aria-hidden="true"
              inert
              className="marquee-track pointer-events-none flex w-max shrink-0 items-start gap-5 pr-5"
            >
              {items.map((item) => (
                <li key={itemKey(item)} className={`${itemClassName} shrink-0`}>
                  {renderItem(item)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}